import { message } from 'antd';
import config from 'config';
import {
  DvmExamListSort,
  DvmExamOutput,
  useGetDvmExamListQuery,
  useRemoveDvmExamMutation,
} from 'gql/types';
import { useSort } from 'modules/common/hooks';
import { GET_DVM_EXAM_LIST_QUERY } from 'modules/donor/graphql/queries';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { useTranslation } from 'react-i18next';

export const useDonorDvmExams = (donorId: string | undefined) => {
  const { t } = useTranslation('donor.DonorDvmExam');
  const pagination = usePagination();
  const sort = useSort<DvmExamListSort>();

  const dvmExamListVariables = {
    input: {
      take: config.PAGINATION.PAGE_SIZE,
      skip: pagination.skip,
      sort: sort.options,
      filter: {
        donorId,
      },
    },
  };

  const getDvmExamQuery = useGetDvmExamListQuery({
    variables: dvmExamListVariables,
    onCompleted: (result) => {
      pagination.setTotal(result.getDvmExamList.meta.total);
    },
    skip: !Boolean(donorId),
  });

  const [removeDvmExam, removeDvmExamMutation] = useRemoveDvmExamMutation({
    refetchQueries: [
      { query: GET_DVM_EXAM_LIST_QUERY, variables: dvmExamListVariables },
    ],
    onCompleted: () => {
      message.success(t('dvmExamHasBeenDeleted'));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const removeDvmExamHandler = (id: string) => {
    removeDvmExam({
      variables: {
        input: { id },
      },
      update(cache) {
        cache.evict({ fieldName: 'getDvmExamList' });
      },
    });
  };

  return {
    dvmExams: getDvmExamQuery.data?.getDvmExamList.data as DvmExamOutput[],
    isLoading: [getDvmExamQuery, removeDvmExamMutation].some(
      ({ loading }) => loading
    ),
    removeDvmExamHandler,
    pagination,
    sort,
  };
};
